import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api';

interface TestConnectionStepProps {
  did: string;
  onSuccess: (apiEndpoint: string) => void;
  onBack: () => void;
  onCancel: () => void;
}

const TestConnectionStep: React.FC<TestConnectionStepProps> = ({
  did,
  onSuccess,
  onBack,
  onCancel,
}) => {
  const [testing, setTesting] = useState(true);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [apiEndpoint, setApiEndpoint] = useState<string>('');

  const testConnection = async () => {
    try {
      setTesting(true);
      setError('');

      const response = await apiClient.post('/mpx-tenants/test-connection', {
        did: did,
      });

      setTestResult(response.data);
      
      if (response.data.success && response.data.api_endpoint) {
        setApiEndpoint(response.data.api_endpoint);
      } else {
        setError(response.data.message || 'Connection test failed');
      }
    } catch (err: any) {
      console.error('Failed to test MPX connection:', err);
      setError(err.message || 'Failed to resolve DID and connect to MPX API. Please check the DID and try again.');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    testConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    testConnection();
  };

  const handleProceed = () => {
    if (testResult?.success && apiEndpoint) {
      onSuccess(apiEndpoint);
    }
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        {testing && (
          <div>
            <h4 className="mb-3">Testing MPX Connection</h4>
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden"></span>
              </div>
              <p className="text-muted">Resolving DID and testing MPX API connection...</p>
              <small className="text-muted">
                <code>{did}</code>
              </small>
            </div>
          </div>
        )}

        {!testing && error && (
          <div>
            <h4 className="mb-3">Testing MPX Connection</h4>
            <div className="alert alert-danger">
              <h5 className="mb-2">
                <i className="fas fa-exclamation-triangle"></i>
                Connection Failed
              </h5>
              <p>{error}</p>
            </div>
            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
              >
                <i className="fas fa-arrow-left mr-1"></i> Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRetry}
              >
                <i className="fas fa-redo mr-1"></i> Retry
              </button>
            </div>
          </div>
        )}

        {!testing && !error && testResult?.success && (
          <div>
            <h4 className="mb-3">Testing MPX Connection</h4>

            <div className="alert alert-success">
              <i className="fas fa-check-circle mr-2"></i>
              <strong>Connection Successful!</strong> The MPX Control Plane API is responding as expected.
            </div>

            <div className="mb-4">
              <h6 className="font-weight-bold mb-2">Connection Details:</h6>
              <div className="card bg-light">
                <div className="card-body">
                  <dl className="row mb-0">
                    <dt className="col-sm-4">MPX DID:</dt>
                    <dd className="col-sm-8">
                      <code>{did}</code>
                    </dd>
                    <dt className="col-sm-4">Resolved API Endpoint:</dt>
                    <dd className="col-sm-8">
                      <code>{apiEndpoint}</code>
                    </dd>
                    {testResult.challenge_endpoint && (
                      <>
                        <dt className="col-sm-4">Auth Challenge Endpoint:</dt>
                        <dd className="col-sm-8">
                          <code>{testResult.challenge_endpoint}</code>
                        </dd>
                      </>
                    )}
                    {testResult.response_time && (
                      <>
                        <dt className="col-sm-4">Response Time:</dt>
                        <dd className="col-sm-8">
                          {testResult.response_time}ms
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
              >
                <i className="fas fa-arrow-left mr-1"></i> Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleProceed}
              >
                Next <i className="fas fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestConnectionStep;
